import React, { useState, useEffect } from 'react';
import { 
  MapPinIcon,
  ShieldCheckIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import Dialog from '../common/Dialog';
import { User, Role } from '../../services/users';
import { userAssignmentsService, UserSiteAssignment } from '../../services/userAssignments';
import siteService, { Site } from '../../services/sites';

export interface UserSiteRolesModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

const UserSiteRolesModal: React.FC<UserSiteRolesModalProps> = ({
  isOpen,
  onClose,
  user
}) => {
  const [sites, setSites] = useState<Site[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [userAssignments, setUserAssignments] = useState<UserSiteAssignment[]>([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAssignment, setNewAssignment] = useState({
    siteId: '',
    roleId: '',
    sessionType: 'PERMANENTE' as 'PERMANENTE' | 'TEMPORAIRE',
    isDefault: false,
    startDate: '',
    endDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Charger les sites et rôles au montage
  useEffect(() => {
    if (isOpen) {
      loadSites();
      loadRoles();
    }
  }, [isOpen]);

  // Charger les assignations de l'utilisateur
  useEffect(() => {
    if (isOpen && user) {
      loadUserAssignments();
    }
  }, [isOpen, user]);

  const loadSites = async () => {
    try {
      setIsLoadingSites(true);
      const sitesData = await siteService.getSites();
      setSites(sitesData || []);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des sites:', error);
    } finally {
      setIsLoadingSites(false);
    }
  };

  const loadRoles = async () => {
    try {
      setIsLoadingRoles(true);
      // Utiliser le service utilisateur pour récupérer les rôles
      const rolesData = await import('../../services/users').then(module => 
        module.default.getRoles()
      );
      
      // Gérer les deux types de réponses possibles
      if (Array.isArray(rolesData)) {
        setRoles(rolesData);
      } else if (rolesData && typeof rolesData === 'object' && 'results' in rolesData) {
        // Réponse paginée
        setRoles(rolesData.results || []);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des rôles:', error);
      setRoles([]);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const loadUserAssignments = async () => {
    if (!user) return;
    
    try {
      setIsLoadingAssignments(true);
      const assignments = await userAssignmentsService.getAssignments(user.id);
      setUserAssignments(assignments);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des assignations:', error);
      setUserAssignments([]);
    } finally {
      setIsLoadingAssignments(false);
    }
  };

  const handleAddAssignment = async () => {
    if (!user || !newAssignment.siteId || !newAssignment.roleId) return;

    try {
      setIsSubmitting(true);
      await userAssignmentsService.createAssignment(user.id, {
        user: parseInt(user.id),
        site: parseInt(newAssignment.siteId),
        role: parseInt(newAssignment.roleId),
        session_type: newAssignment.sessionType,
        starts_at: newAssignment.startDate || undefined,
        ends_at: newAssignment.endDate || undefined,
        is_default: newAssignment.isDefault
      });

      // Recharger les assignations
      await loadUserAssignments();
      
      // Réinitialiser le formulaire
      setNewAssignment({
        siteId: '',
        roleId: '',
        sessionType: 'PERMANENTE',
        isDefault: false,
        startDate: '',
        endDate: ''
      });
      setShowAddForm(false);
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'ajout de l\'assignation:', error);
      alert(`Erreur lors de l'ajout: ${error.message || 'Erreur inconnue'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId: number) => {
    if (!user || !confirm('Êtes-vous sûr de vouloir retirer cet utilisateur de ce site ?')) return;

    try {
      await userAssignmentsService.deleteAssignment(user.id, assignmentId.toString());
      await loadUserAssignments();
    } catch (error: any) {
      console.error('❌ Erreur lors de la suppression:', error);
      alert(`Erreur lors de la suppression: ${error.message || 'Erreur inconnue'}`);
    }
  };

  const handleSetDefaultSite = async (assignmentId: number) => {
    if (!user || !confirm('Définir ce site comme site par défaut pour cet utilisateur ?')) return;

    try {
      await userAssignmentsService.setDefaultSite(user.id, assignmentId.toString());
      await loadUserAssignments();
    } catch (error: any) {
      console.error('❌ Erreur lors de la définition du site par défaut:', error);
      alert(`Erreur lors de la définition: ${error.message || 'Erreur inconnue'}`);
    }
  };

  const getSiteName = (siteId: number) => {
    const site = sites.find(s => s.id === siteId);
    return site ? site.name : `Site ${siteId}`;
  };

  const getRoleLabel = (roleId: number) => {
    if (!Array.isArray(roles)) {
      return `Rôle ${roleId}`;
    }
    const role = roles.find(r => r.id === roleId.toString() || r.id === roleId);
    return role ? role.label : `Rôle ${roleId}`;
  };

  if (!user) return null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Rôles par site"
      size="xl"
    >
      <div className="space-y-6">
        {/* En-tête avec informations utilisateur */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600 font-medium text-sm">
                {user.name?.split(' ').map((n: string) => n[0]).join('') || user.username?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">{user.name || 'Nom non défini'}</h3>
              <p className="text-sm text-gray-500">@{user.username}</p>
              {user.email && (
                <p className="text-sm text-gray-500">{user.email}</p>
              )}
            </div>
          </div>
        </div>

        {/* Bouton d'ajout d'assignation */}
        <div className="flex justify-between items-center">
          <h4 className="text-lg font-medium text-gray-900">Assignations actuelles</h4>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Ajouter une assignation
          </button>
        </div>

        {/* Formulaire d'ajout d'assignation */}
        {showAddForm && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <h5 className="font-medium text-gray-900">Nouvelle assignation</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site *</label>
                <select
                  value={newAssignment.siteId}
                  onChange={(e) => setNewAssignment(prev => ({ ...prev, siteId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={isLoadingSites}
                >
                  <option value="">Sélectionner un site</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>
                      {site.name} ({site.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle *</label>
                <select
                  value={newAssignment.roleId}
                  onChange={(e) => setNewAssignment(prev => ({ ...prev, roleId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={isLoadingRoles}
                >
                  <option value="">Sélectionner un rôle</option>
                  {Array.isArray(roles) && roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de session</label>
                <select
                  value={newAssignment.sessionType}
                  onChange={(e) => setNewAssignment(prev => ({ 
                    ...prev, 
                    sessionType: e.target.value as 'PERMANENTE' | 'TEMPORAIRE' 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="PERMANENTE">Permanente</option>
                  <option value="TEMPORAIRE">Temporaire</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={newAssignment.isDefault}
                  onChange={(e) => setNewAssignment(prev => ({ ...prev, isDefault: e.target.checked }))}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
                  Site par défaut
                </label>
              </div>
            </div>

            {newAssignment.sessionType === 'TEMPORAIRE' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                  <input
                    type="date"
                    value={newAssignment.startDate}
                    onChange={(e) => setNewAssignment(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                  <input
                    type="date"
                    value={newAssignment.endDate}
                    onChange={(e) => setNewAssignment(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleAddAssignment}
                disabled={!newAssignment.siteId || !newAssignment.roleId || isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Ajout...' : 'Ajouter'}
              </button>
            </div>
          </div>
        )}

        {/* Liste des assignations */}
        <div className="space-y-4">
          {isLoadingAssignments ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Chargement des assignations...</p>
            </div>
          ) : userAssignments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MapPinIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p>Aucune assignation de site trouvée</p>
              <p className="text-sm">Cliquez sur "Ajouter une assignation" pour commencer</p>
            </div>
          ) : (
            userAssignments.map((assignment) => (
              <div
                key={assignment.id}
                className={`border rounded-lg p-4 ${
                  assignment.is_default ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <MapPinIcon className="h-5 w-5 text-gray-400" />
                      <div>
                        <h5 className="font-medium text-gray-900">
                          {getSiteName(assignment.site)}
                        </h5>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <ShieldCheckIcon className="h-4 w-4 mr-1" />
                            {getRoleLabel(assignment.role)}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            assignment.session_type === 'PERMANENTE' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {assignment.session_type === 'PERMANENTE' ? 'Permanente' : 'Temporaire'}
                          </span>
                          {assignment.is_default && (
                            <span className="flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Site par défaut
                            </span>
                          )}
                        </div>
                        {assignment.session_type === 'TEMPORAIRE' && (
                          <div className="mt-2 text-xs text-gray-500">
                            Du {assignment.starts_at ? new Date(assignment.starts_at).toLocaleDateString() : 'N/A'} 
                            au {assignment.ends_at ? new Date(assignment.ends_at).toLocaleDateString() : 'N/A'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {!assignment.is_default && (
                      <button
                        onClick={() => handleSetDefaultSite(assignment.id)}
                        className="p-2 text-orange-600 hover:text-orange-700 hover:bg-orange-100 rounded-lg transition-colors"
                        title="Définir comme site par défaut"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveAssignment(assignment.id)}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-colors"
                      title="Retirer l'assignation"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default UserSiteRolesModal;
